import { useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useGiftContext } from './Context/GiftContextProvider';
import DSBox from '../../design-system/DSBox';
import DSImageList from '../../design-system/DSImageList';
import DSImageListItem from '../../design-system/DSImageListItem';
import { GiftKlydo } from '../../api/gift';
import { KLYDO_YELLOW } from '../../consts/colors';
import { getDeviceAppropriateAnimationUrl } from './utils';
import { isMobile } from 'react-device-detect';

// ImageListItem forces an inline height ('auto' unless the list has a
// rowHeight), so the tile's sx height alone never applies - the tile takes
// its height from the video and turns into an ellipse while it loads.
const GREET_TILE_SIZE = 129;

const KlydoImage = ({ id, url }: { id: string; url: string }) => {
  const [videoSrc, setVideoSrc] = useState('');

  useEffect(() => {
    const abortController = new AbortController();
    let blobUrl: string | undefined;
    (async () => {
      try {
        const response = await fetch(url, { signal: abortController.signal });
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);
        setVideoSrc(blobUrl);
      } catch {
        // ignore abort/fetch errors
      }
    })();
    return () => {
      abortController.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);

  return (
    <video
      key={id}
      muted
      autoPlay
      loop
      playsInline
      style={{
        borderRadius: '50%',
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
      src={videoSrc}
    />
  );
};

const GiftKlydoGreet = () => {
  const theme = useTheme();
  const {
    selectedKlydoGreet,
    setSelectedKlydoGreet,
    selectedOccasion,
    klydoGiftProps,
    next,
    setKlydoGiftProps,
  } = useGiftContext();
  const handleSelectedKlydoGreet = useCallback(
    (klydo: GiftKlydo) => {
      setSelectedKlydoGreet(klydo);
      setKlydoGiftProps({ ...klydoGiftProps, klydoId: klydo.id });
      next();
    },
    [setSelectedKlydoGreet, setKlydoGiftProps, klydoGiftProps, next],
  );

  return (
    <DSBox
      sx={{
        paddingTop: { xs: 1, md: 8 },
        justifyContent: 'center',
        display: 'flex',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <DSImageList
        cols={isMobile ? 2 : 5}
        gap={isMobile ? 15 : 50}
        rowHeight={GREET_TILE_SIZE}
        sx={{ width: { xs: 300, md: 850 } }}
      >
        {(selectedOccasion?.klydos ?? []).map((klydoGreet) => (
          <DSImageListItem
            key={klydoGreet.id}
            onClick={() => handleSelectedKlydoGreet(klydoGreet)}
            sx={{
              width: GREET_TILE_SIZE,
              border: `4px solid ${
                selectedKlydoGreet?.id === klydoGreet.id
                  ? KLYDO_YELLOW
                  : 'white'
              }`,
              borderRadius: '50%',
              cursor: 'pointer',
            }}
          >
            <KlydoImage
              id={klydoGreet.id}
              url={getDeviceAppropriateAnimationUrl(klydoGreet)}
            />
          </DSImageListItem>
        ))}
      </DSImageList>
    </DSBox>
  );
};

export default GiftKlydoGreet;
